import React, { useState, useEffect, useRef } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import SettingsSection from './SettingsSection'
import DateInput, { isValidDate } from './DateInput'
import CurrencyInput from './CurrencyInput'
import LocationSelect, { LocationValue, EMPTY_LOCATION } from './LocationSelect'
import MappingList, { InPlaceEditor } from './MappingList'
import { useApiData, LoadError, Loading } from './useApiData'
import {
    apiGetPrepaidCompetitionConfig, apiSetPrepaidCompetitionScalars,
    apiPutPrepaidCompetitionPrice, apiDeletePrepaidCompetitionPrice,
    PrepaidCompetitionConfigData, PrepaidCompetitionPriceLocation,
} from './Api'
import { useDebouncedChainedCallback } from './CallCenter'
import { useScopeKeys, scopeKey, scopedTree } from './Scope'
import formatCurrency from './Formatting'

type PriceLocation = { amount: number, income_account: number, cost_center_item_id: string, project_id: string }

// Without the amount, which the editor fills in: a part is complete as a *location* long before
// anybody has said how much of the price goes to it.
const toLocation = (p: LocationValue): Omit<PriceLocation, 'amount'> | null =>
    (p.cost_center_item_id !== null && p.project_id !== null && p.account !== null)
        ? { cost_center_item_id: p.cost_center_item_id, project_id: p.project_id, income_account: p.account }
        : null
const fromLocation = (r: Omit<PriceLocation, 'amount'>): LocationValue =>
    ({ cost_center_item_id: r.cost_center_item_id, project_id: r.project_id, account: r.income_account })

// One part as a human one-liner (account · kostnadsställe · projekt), like SwishSettings.
const summarizePart = (data: PrepaidCompetitionConfigData,
                       loc: Omit<PriceLocation, 'amount'>): string => {
    const acct = data.official_income_accounts.find(a => a.account_number === loc.income_account)
    const acctText = acct ? `${acct.account_number} ${acct.account_name}` : String(loc.income_account)
    // Names from the complete maps (not the picker tree), so a stored row outside the active
    // role's scope still shows names rather than raw GUIDs.
    const ccName = data.cost_center_names[loc.cost_center_item_id] ?? loc.cost_center_item_id
    const projName = data.project_names[loc.project_id] ?? loc.project_id
    return `${acctText} · ${ccName} · ${projName}`
}

// The whole price. One part reads exactly as it always did; several say how much goes where,
// because "9 250 kr books as 3017" would be half true and the half left out is the interesting one.
const summarize = (data: PrepaidCompetitionConfigData, row: PrepaidCompetitionPriceLocation): string =>
    row.parts.length === 1
        ? summarizePart(data, row.parts[0])
        : row.parts.map(part => `${formatCurrency(part.amount)} kr → ${summarizePart(data, part)}`)
              .join('   +   ')

/**
 * Why the Kostnadsställe dropdown would be empty, or null when it is not.
 *
 * Two quite different causes look the same on screen, and neither is the user's to fix:
 * the active role owns no (cost-centre, project) pair at all in this company, or it owns some but
 * none of those projects has an income account, so there is nothing income can be booked on.
 *
 * Which is which is worth saying, because they are put right in different places by different
 * people — the role editor, or Konton per projekt.
 */
function whyNothingToPick(data: PrepaidCompetitionConfigData,
                          scopeKeys: ReadonlySet<string> | null): string | null {
    if (scopedTree(data.cost_center_items, scopeKeys, null, null).length > 0) return null
    if (data.cost_center_items.length === 0) {
        return 'Inga projekt har något intäktskonto i den här föreningen, så det finns ingenting '
             + 'att bokföra på. En administratör lägger upp dem under Inställningar → Företag, '
             + 'Konton per projekt.'
    }
    return 'Din roll har inga ansvarsområden med intäktskonton i den här föreningen, så det finns '
         + 'inga kostnadsställen att välja. En administratör kan lägga till dem under '
         + 'Inställningar → Roller & behörigheter — ett kostnadsställe räknas först när minst ett '
         + 'av dess projekt är ikryssat.'
}

// The shared three-in-one selector bound to the config's income data (see SwishSettings.IncomeLocation).
function IncomeLocation({ data, value, onChange, disabled }: {
    data: PrepaidCompetitionConfigData, value: LocationValue, onChange: (next: LocationValue) => void, disabled?: boolean,
}) {
    const accountName = (n: number) => {
        const a = data.official_income_accounts.find(a => a.account_number === n)
        return a ? `${a.account_number} – ${a.account_name}` : String(n)
    }
    return <LocationSelect costCenterItems={data.cost_center_items}
        accountNumbersOf={p => p.income_account_numbers} accountName={accountName}
        value={value} onChange={onChange} disabled={disabled}/>
}

// One editable part: how much of the price books here, and where.
type PartDraft = { amount: number | null, loc: LocationValue }

const ore = (kronor: number) => Math.round(kronor * 100)

/**
 * Add/edit a price and the parts it books as.
 *
 * A price usually has one part worth the whole price, and that is what this looks like until
 * somebody presses "Dela upp". It has more when one figure on the Eventor order is several things:
 * a team entered late arrives as 9 250 kr, which is an 8 520 kr entry fee and a 730 kr late charge
 * on different locations. The price is all Eventor says about what was sold, so the split lives
 * here rather than in the import.
 *
 * The parts must add up to the price — the server refuses a save that does not, and the sum is
 * shown here so nobody has to discover that by pressing Spara.
 */
function PriceEditor({ data, initialPrice, initialParts, disabled, onCancel, onSave }: {
    data: PrepaidCompetitionConfigData, initialPrice: number | null, initialParts: PartDraft[],
    disabled: boolean,
    onCancel: () => void, onSave: (price: number, parts: PriceLocation[]) => void,
}) {
    const [price, setPrice] = useState<number | null>(initialPrice)
    const [parts, setParts] = useState<PartDraft[]>(initialParts)
    const unavailable = whyNothingToPick(data, useScopeKeys())
    const split = parts.length > 1

    const at = (index: number, next: Partial<PartDraft>) =>
        setParts(parts.map((part, i) => i === index ? { ...part, ...next } : part))
    // Splitting hands the whole price to the first part and nothing to the new one, so the sum is
    // wrong until somebody says how it divides — which is the question being asked.
    const addPart = () => setParts([...parts, { amount: null, loc: EMPTY_LOCATION }])
    const removePart = (index: number) => setParts(parts.filter((_, i) => i !== index))

    const located = parts.map(part => toLocation(part.loc))
    const complete = located.every(loc => loc !== null)
        && (!split || parts.every(part => part.amount !== null && ore(part.amount) !== 0))
    const priceOk = price !== null && price > 0
    // A single part is the price by definition, so its amount is not asked for and cannot disagree.
    const total = split ? parts.reduce((sum, part) => sum + ore(part.amount ?? 0), 0) : ore(price ?? 0)
    const adds_up = !priceOk || total === ore(price ?? 0)

    return (
        <InPlaceEditor disabled={disabled} canSave={priceOk && complete && adds_up} onCancel={onCancel}
            onSave={() => {
                if (price === null || !complete) return
                onSave(price, parts.map((part, i) => ({
                    ...(located[i] as PriceLocation),
                    amount: split ? (part.amount as number) : price,
                })))
            }}>
            <CurrencyInput label="Pris (kr)" value={price} disabled={disabled} onChange={setPrice}/>
            {/* An empty Kostnadsställe dropdown is a dead end: it looks identical whether the role
                owns nothing here, or nothing it owns can take income. Both are somebody else's to
                put right, so the picker says which — Elin met the first and there was nothing on
                screen to tell her so. */}
            {unavailable !== null && <Alert severity="info" sx={{ mb: 1 }}>{unavailable}</Alert>}
            {parts.map((part, index) =>
                <Box key={index} sx={{ display: 'flex', flexFlow: 'row wrap', gap: 1,
                                       alignItems: 'center' }}>
                    {split &&
                        <CurrencyInput label="Varav (kr)" value={part.amount} disabled={disabled}
                            onChange={amount => at(index, { amount })}/>}
                    <IncomeLocation data={data} value={part.loc} disabled={disabled}
                        onChange={loc => at(index, { loc })}/>
                    {split &&
                        <Button size="small" color="error" disabled={disabled}
                                onClick={() => removePart(index)}>Ta bort delen</Button>}
                </Box>)}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button size="small" disabled={disabled} onClick={addPart}>
                    {split ? '+ Lägg till en del' : 'Dela upp på flera platser'}
                </Button>
                {/* Said while it is wrong, not after Spara: the server refuses a split that does
                    not add up, and finding that out by pressing the button teaches nothing. */}
                {split && priceOk && !adds_up &&
                    <Typography variant="body2" color="error">
                        Delarna summerar till {formatCurrency(total / 100)} kr av
                        {' '}{formatCurrency(price ?? 0)} kr.
                    </Typography>}
            </Box>
        </InPlaceEditor>
    )
}

// The stored scalars: the invoice due date. Auto-saved on a debounce (the Swish-scalars pattern).
// The payment event is picked live in the sync view; the from/to window is derived.
function Scalars({ data }: { data: PrepaidCompetitionConfigData }) {
    const [dueDate, setDueDate] = useState(data.scalars?.due_date ?? '')
    const changedRef = useRef(false)

    const debouncedSave = useDebouncedChainedCallback(async () => {
        if (!isValidDate(dueDate)) return
        await apiSetPrepaidCompetitionScalars({ due_date: dueDate })
    }, 1500)
    useEffect(() => { if (changedRef.current) { changedRef.current = false; debouncedSave() } })

    return (
        <Box sx={{ maxWidth: 480 }}>
            <DateInput label="Förfallodatum på Visma-fakturorna" value={dueDate}
                onChange={v => { setDueDate(v); changedRef.current = true }}/>
        </Box>
    )
}

function ConfigBody({ data, reload }: { data: PrepaidCompetitionConfigData, reload: () => void }) {
    const [busy, setBusy] = useState(false)
    const scopeKeys = useScopeKeys()
    // Every part, not the first: a price split across two locations is only yours if both are.
    const inScope = (row: PrepaidCompetitionPriceLocation) =>
        scopeKeys === null
        || row.parts.every(part => scopeKeys.has(scopeKey(part.cost_center_item_id, part.project_id)))

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true)
        try { await action(); reload() } catch { /* on the error bus */ }
        finally { setBusy(false) }
    }
    // Editing a price = the key changed → delete the old row then upsert the new (one action).
    const savePrice = (originalPrice: number | null, price: number, parts: PriceLocation[]) =>
        run(async () => {
            if (originalPrice !== null && originalPrice !== price) await apiDeletePrepaidCompetitionPrice(originalPrice)
            await apiPutPrepaidCompetitionPrice({ unit_price: price, parts })
        })

    return (
        <>
            {/* The tab sits under the competition selector, so it reads as belonging to whichever
                competition is chosen. It does not: both tables are company-wide. Saying so is the
                cheap half of the fix; scoping them per competition would also make these settings
                unreachable whenever Eventor is down, since the selector is fetched from there. */}
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Inställningarna gäller alla förskottstävlingar — valet ovanför påverkar dem inte.
            </Typography>
            {/* "Fakturor" named the one thing every part of this feature touches, and meant only
                one of them: Eventor's orders to the clubs, the customer invoices Eko creates from
                them in Visma, and the invoices a service correction has Eventor raise are three
                different documents. The heading now says which of them these settings are about. */}
            <SettingsSection title="Import till Visma"
                description={'Eko läser beställningar från verksamhetsårets början till idag och '
                    + 'sätter samma förfallodatum på alla kundfakturor den skapar. Eventors eget '
                    + 'datum används inte. Ändringar sparas automatiskt.'}>
                <Scalars data={data}/>
            </SettingsSection>
            {/* Why a price and not a product: the price is all Eventor's order rows carry that
                says what was sold — the same figure is the whole identity of a lagavgift, a
                vindskyddsplats or a logi. */}
            <SettingsSection title="Pris → konto"
                description={'Importen bokför varje belopp från Eventor på den plats du väljer '
                    + 'här, och avstämningen visar samma koppling. Priset är det enda Eventor '
                    + 'säger om vad en post är. Du kan bara ändra rader inom ditt ansvarsområde.'}>
                <MappingList<PrepaidCompetitionPriceLocation>
                rows={data.price_locations}
                getKey={r => String(r.unit_price)}
                renderName={r => formatCurrency(r.unit_price)}
                getSummary={r => summarize(data, r)}
                nameHeader="Pris" locationHeader="Bokförs på"
                canRemove={r => inScope(r)} canEdit={r => inScope(r)}
                onRemove={r => run(() => apiDeletePrepaidCompetitionPrice(r.unit_price))}
                addLabel="+ Lägg till pris…"
                disabled={busy}
                emptyText="Inga priser konfigurerade."
                renderEditor={(editing, close) => {
                    const original = editing.mode === 'edit' ? editing.row : null
                    return <PriceEditor data={data}
                        initialPrice={original?.unit_price ?? null}
                        initialParts={original !== null
                            ? original.parts.map(part => ({ amount: part.amount, loc: fromLocation(part) }))
                            : [{ amount: null, loc: EMPTY_LOCATION }]}
                        disabled={busy} onCancel={close}
                        onSave={(price, parts) => savePrice(original?.unit_price ?? null, price, parts).then(close)}/>
                }}
                />
            </SettingsSection>
        </>
    )
}

// The Inställningar sub-tab of Förskottstävlingar. Always shown (the tab is the toggle now); its
// data is a fast DB+Visma read loaded on mount. The slow payment-events scrape lives in the Synk
// view, not here.
export default function PrepaidCompetitionConfigEditor() {
    const config = useApiData(apiGetPrepaidCompetitionConfig, [])
    return (
        <Box sx={{ p: 1, maxWidth: '60rem' }}>
            {config.loading && config.data === null && <Loading/>}
            {config.error !== null && !config.loading && <LoadError onRetry={config.reload}/>}
            {config.data !== null && <ConfigBody data={config.data} reload={config.reload}/>}
        </Box>
    )
}
